import { useCallback, useContext, useMemo } from 'react'
import { ScenarioStatesProviderContext } from '../providers/StatesProvider'
import * as Cesium from 'cesium'
import { hideShowAxisAndLabels, setPredictionVisualsVisibility } from '../utils/viewer-packets'
import { requestRender } from '../../../../utils/cesiumHelperFunctions'
import useViewer from './useViewer'

let predictionStopInProgress = false
const STOP_RENDER_SETTLE_FRAMES = 2
const STOP_UNLOCK_DELAY = 150

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const waitForNextPaint = () => new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame !== 'function') {
        setTimeout(resolve, 0)
        return
    }

    requestAnimationFrame(() => resolve())
})

const waitForViewerFrames = async (viewer: Cesium.Viewer, frames = STOP_RENDER_SETTLE_FRAMES) => {
    for (let index = 0; index < frames; index += 1) {
        if (viewer.isDestroyed()) return
        requestRender(viewer)
        await waitForNextPaint()
    }
}

const setPredictionCleanupInProgress = (state: boolean) => {
    (window as any).predictionCleanupInProgress = state
}

const useOrbitAnimationDetails = () => {
    const context = useContext(ScenarioStatesProviderContext)
    if (context === undefined) {
        throw new Error('useOrbitAnimationDetails must be used within a Scenario States Provider')
    }
    const { viewer, updateViewerMode } = useViewer()
    const { orbitAnimationDetails, setOrbitAnimationDetails } = context
    const animationRunning = useMemo(() => orbitAnimationDetails.startAnimation, [orbitAnimationDetails.startAnimation])

    const updateViewerStep = useCallback((step: number) => {
        if (!viewer) return
        viewer.clock.shouldAnimate = true
        viewer.clock.clockStep = Cesium.ClockStep.SYSTEM_CLOCK_MULTIPLIER
        viewer.clock.multiplier = step

        setOrbitAnimationDetails(prevState => ({ ...prevState, animationSpeed: step, controllerState: 'play' }))
        requestRender(viewer)
    }, [viewer, setOrbitAnimationDetails])

    const updateViewerState = useCallback((state: 'play' | 'pause') => {
        if (!viewer) return
        setOrbitAnimationDetails(prevState => {
            viewer.clock.shouldAnimate = true
            viewer.clock.clockStep = Cesium.ClockStep.SYSTEM_CLOCK_MULTIPLIER
            viewer.clock.multiplier = state === 'play' ? prevState.animationSpeed : 0
            return { ...prevState, controllerState: state }
        })
        requestRender(viewer)
    }, [viewer, setOrbitAnimationDetails])

    const setAnimationStartTimeToViewer = useCallback((startTime: number, selectedSatellite: string | undefined) => {
        if (!viewer) return
        const julianDate = Cesium.JulianDate.fromDate(new Date(startTime * 1000));
        viewer.clock.currentTime = julianDate
        // Commenting out zoom function as it is creating issue while starting the presim from mid way
        // zoomIntoTheEntity(viewer, 'PREDICTION_ORBIT', 'pred' + selectedSatellite)
    }, [viewer])

    const getViewerCurrentTime = useCallback(() => {
        let epochTime: number = 0
        if (!viewer || viewer.isDestroyed()) return epochTime
        const clock = viewer?.clock;

        if (clock && clock?.currentTime) {
            const currentTime = clock?.currentTime;
            epochTime = Cesium.JulianDate.toDate(currentTime).getTime() / 1000;
        }
        return epochTime
    }, [viewer])

    const onAxisControlChange = useCallback(() => {
        if (!viewer) return;
        setOrbitAnimationDetails((prev) => {
            const axisControl = prev?.showAxisControl
            hideShowAxisAndLabels(viewer, !axisControl);
            return {
                ...prev,
                showAxisControl: !axisControl
            }
        })
    }, [viewer, setOrbitAnimationDetails]);

    const stopPresimAnimation = useCallback(async () => {
        if (!viewer || !animationRunning || predictionStopInProgress) return
        predictionStopInProgress = true
        setPredictionCleanupInProgress(true)

        try {
            if (viewer.isDestroyed()) return

            viewer.clock.multiplier = 0;
            setPredictionVisualsVisibility(viewer, false)

            await waitForViewerFrames(viewer)
            if (viewer.isDestroyed()) return

            viewer.clock.currentTime = Cesium.JulianDate.clone(viewer.clock.startTime);
            updateViewerMode('2D')

            setOrbitAnimationDetails((prev) => ({
                ...prev,
                startAnimation: false,
                animationSpeed: 2,
                controllerState: 'pause',
                showAnimationDetails: false,
                expandedView: false
            }));
        } finally {
            await wait(STOP_UNLOCK_DELAY)
            predictionStopInProgress = false
            setPredictionCleanupInProgress(false)
            requestRender(viewer)
        }
    }, [animationRunning, setOrbitAnimationDetails, updateViewerMode, viewer])

    return { orbitAnimationDetails, setOrbitAnimationDetails, setAnimationStartTimeToViewer, getViewerCurrentTime, updateViewerStep, updateViewerState, onAxisControlChange, stopPresimAnimation }
}

export default useOrbitAnimationDetails
